import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Section,
  Eyebrow,
  Headline,
  Lead,
} from '../components/iss/IssPrimitives';
import apiService, { type PocSummaryResponse } from '../service/ApiService';
import { EmptyState, PhasePill, PhaseTabs, SearchField } from './PocListParts';
import {
  formatDate,
  formatLabel,
  matchesPocSearch,
  resumePathForPhase,
  shortId,
  type PhaseFilter,
} from './pocListUtils';
import './MyPocsPage.css';

const MyPocsPage: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<PocSummaryResponse[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [phaseFilter, setPhaseFilter] = useState<PhaseFilter>('ALL');
  const [idQuery, setIdQuery] = useState('');
  const [nameQuery, setNameQuery] = useState('');

  const openPoc = (poc: PocSummaryResponse) => {
    navigate(resumePathForPhase(poc.currentPhase), {
      state: {
        pocId: poc.id,
        title: poc.title,
        businessJustification: poc.title
          ? { title: poc.title }
          : undefined,
      },
    });
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await apiService.getMyPocs({ page: 0, size: 50 });
        if (cancelled) return;
        setItems(page.content ?? []);
        setTotalElements(page.totalElements ?? 0);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError('Could not load your PoCs. Check that the API is running and you are signed in.');
          setItems([]);
          setTotalElements(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const phaseCounts = useMemo(() => {
    const counts: Record<PhaseFilter, number> = {
      ALL: items.length,
      INITIATION: 0,
      PREPARATION: 0,
      RUNNING: 0,
      EVALUATION: 0,
    };
    for (const poc of items) {
      const phase = poc.currentPhase as PhaseFilter;
      if (phase in counts && phase !== 'ALL') {
        counts[phase] += 1;
      }
    }
    return counts;
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((poc) => {
      if (phaseFilter !== 'ALL' && poc.currentPhase !== phaseFilter) {
        return false;
      }
      return matchesPocSearch(poc, idQuery, nameQuery);
    });
  }, [items, phaseFilter, idQuery, nameQuery]);

  const clearFilters = () => {
    setPhaseFilter('ALL');
    setIdQuery('');
    setNameQuery('');
  };

  const hasActiveFilters =
    phaseFilter !== 'ALL' || idQuery.trim() !== '' || nameQuery.trim() !== '';


  return (
    <div className="poc-list">
      <Section theme="dark" spacing="tight" className="poc-list__hero">
        <Eyebrow>PoC library</Eyebrow>
        <Headline as="h1" size="3xl">
          My PoCs
        </Headline>
        <Lead>
          PoCs you own. Filter by phase or search by ID or name. Click a row
          to continue in that PoC&apos;s current phase.
        </Lead>
        <PhaseTabs
          active={phaseFilter}
          counts={phaseCounts}
          showClear={hasActiveFilters}
          onSelect={setPhaseFilter}
          onClear={clearFilters}
        />
      </Section>

      <Section theme="light" spacing="tight">
        <div className="poc-list__panel">
          <div className="poc-list__toolbar">
            <div className="poc-list__searches">
              <SearchField
                label="Search for PoC ID"
                placeholder="Search by PoC ID"
                value={idQuery}
                onChange={setIdQuery}
              />
              <SearchField
                label="Search for PoC name"
                placeholder="Search by name"
                value={nameQuery}
                onChange={setNameQuery}
              />
            </div>
            {!loading && !error ? (
              <p className="poc-list__meta">
                Showing {filtered.length}
                {hasActiveFilters ? ' matching' : ''}
                {totalElements > items.length
                  ? ` (loaded ${items.length} of ${totalElements})`
                  : ` of ${items.length}`}
              </p>
            ) : null}
          </div>

          {loading ? (
            <p className="poc-list__status" role="status">
              Loading your PoCs�
            </p>
          ) : null}

          {error ? (
            <p className="poc-list__error" role="alert">
              {error}
            </p>
          ) : null}

          {!loading && !error && filtered.length === 0 ? (
            items.length === 0 ? (
              <EmptyState
                title="You have no PoCs yet."
                text="Start a new PoC from the home page."
                action={{ label: 'Start a new PoC', to: '/pocs/new' }}
              />
            ) : (
              <EmptyState
                title="No PoCs match the current filters."
                action={{ label: 'Clear filters', onClick: clearFilters }}
              />
            )
          ) : null}

          {!loading && !error && filtered.length > 0 ? (
            <div className="poc-list__table-wrap">
              <table className="poc-list__table">
                <thead>
                  <tr>
                    <th scope="col">PoC name</th>
                    <th scope="col">Phase</th>
                    <th scope="col">Status</th>
                    <th scope="col">Domain</th>
                    <th scope="col">Updated</th>
                    <th scope="col">
                      <span className="poc-list__sr-only">Continue</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((poc) => (
                    <tr
                      key={poc.id}
                      className="poc-list__row"
                      tabIndex={0}
                      role="link"
                      aria-label={`Continue ${poc.title || 'PoC'} in ${formatLabel(poc.currentPhase)}`}
                      onClick={() => openPoc(poc)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openPoc(poc);
                        }
                      }}
                    >
                      <td>
                        <span className="poc-list__name">{poc.title || '�'}</span>
                        <span className="poc-list__id" title={poc.id}>
                          {shortId(poc.id)}
                        </span>
                      </td>
                      <td>
                        <PhasePill phase={poc.currentPhase} />
                      </td>
                      <td className="poc-list__muted">{formatLabel(poc.currentStatus)}</td>
                      <td className="poc-list__muted">{formatLabel(poc.businessDomain)}</td>
                      <td className="poc-list__muted">{formatDate(poc.updatedAt)}</td>
                      <td className="poc-list__action-cell">
                        <span className="poc-list__continue" aria-hidden>
                          Continue ?
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </Section>
    </div>
  );
};

export default MyPocsPage;
